import React, { useEffect, useMemo, useState } from 'react';
import useApi from '../../services/useApi';
import GanttChart from './GanttChart';
import styles from './style.module.css';

const STATUS_COLOR = {
  waiting:   '#9E9E9E',
  running:   '#09ff00',
  complete:  '#007bff',
  completed: '#007bff',
  'exclude from aggregation': '#ffc233',
  error:     '#EF5350',
  fail:     '#EF5350',
  timeout:   '#FF7043',
  unknown:   '#9E9E9E',
};

function normalizeStatus(jobStatus, jobState, hasEnd) {
  const js = String(jobStatus ?? '').toLowerCase();
  if (js) {
    if (js === 'complete' || js === 'completed') return 'completed';
    if (js === 'agg_out') return 'exclude from aggregation';
    if (js === 'running') return 'running';
    if (js === 'waiting') return 'waiting';
    if (js === 'timeout' || js === 'time_out') return 'timeout';
    if (js === 'error' || js === 'failed' || js==='fail' || js === 'failure') return 'fail';
  }
  const st = String(jobState ?? '').toUpperCase();
  if (st.includes('TIMEOUT') || st.includes('TIME_OUT')) return 'timeout';
  return hasEnd ? 'completed' : 'running';
}

function msToDurationString(ms = 0) {
  if (!Number.isFinite(ms) || ms <= 0) return '00:00:00';
  const s = Math.floor(ms / 1000);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return [h, m, ss].map(v => String(v).padStart(2, '0')).join(':');
}

function sumDurations(list = [], startKey = 'start', endKey = 'end') {
  let total = 0;
  for (const it of list) {
    const s = it?.[startKey], e = it?.[endKey];
    if (!s || !e) continue;
    const diff = new Date(e) - new Date(s);
    if (Number.isFinite(diff) && diff > 0) total += diff;
  }
  return total;
}

export default function RoundGanttChart({ taskId, deviceId }) {
  const [roundData, setRoundData] = useState({});

  useEffect(() => {
    useApi
      .get('backboard/timeline/task/device?taskId=' + taskId + '&deviceId=' + deviceId)
      .then((res) => {
        setRoundData(res.data);
      })
      .catch(function () {});
  }, []);

  const { devices, startTime, endTime } = useMemo(() => {
    const blocks = Array.isArray(roundData?.deviceTimelines) ? roundData.deviceTimelines : [];

    const rows = blocks
      .filter(b => b && b.roundNum != null && Array.isArray(b.timelines))
      .map((b) => {
        const rn = b.roundNum;
        const tasks = b.timelines
          .slice()
          .sort((a, z) => new Date(a?.startTime ?? 0) - new Date(z?.startTime ?? 0))
          .map((it) => {
            const status = normalizeStatus(it?.jobStatus, it?.jobState, !!it?.endTime);
            const color = STATUS_COLOR[status] ?? STATUS_COLOR.unknown;
            return {
              task: it?.jobState ?? '',         
              roundNum: rn,
              status,
              color,
              start: it?.startTime ?? null,
              end: it?.endTime ?? null,
            };
          });

        const totalMs = sumDurations(tasks, 'start', 'end');
        const summary = `${tasks.length} Jobs • ${msToDurationString(totalMs)}`;

        const last = tasks[tasks.length - 1] || {};
        const rowStatus = last?.status || 'running';

        return {
          name: String(rn)+' Round',
          status: rowStatus,
          tasks,
          summary,
        };
      })
      .sort((a, b) => Number(a.name) - Number(b.name));

    const allStarts = rows.flatMap(d => (d.tasks || []).map(t => +new Date(t.start)).filter(Boolean));
    const allEnds   = rows.flatMap(d => (d.tasks || []).map(t => +new Date(t.end)).filter(Boolean));
    const st = allStarts.length ? new Date(Math.min(...allStarts)) : undefined;
    const et = allEnds.length   ? new Date(Math.max(...allEnds))   : undefined;

    return { devices: rows, startTime: st, endTime: et };
  }, [roundData]);

  return (
    <GanttChart
      className={styles.roundChart}
      devices={devices}
      startTime={startTime}
      endTime={endTime}
      labelSingleLine
      isModal
    />
  );
}